import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FiGrid } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import AppIcon from "./ds/AppIcon";

/**
 * "View apps inside", for a product's owner and moderators: a button on the product that
 * opens the apps it sells the way an iPhone folder opens — growing out of the button over
 * a blurred screen, with the product's name above. Tapping an app opens it in the space.
 *
 * Renders nothing without apps. The parent decides who sees it.
 *
 * @param {{ key, name, icon, color, navId }[]} apps from utils/mintSpaceApps productFolderApps
 */
const ProductAppsFolder = ({ spaceId, title = "", apps = [], className = "", buttonClassName = "" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  const [open, setOpen] = useState(false);
  /** Where the folder grows from: the button's centre, relative to the screen's. */
  const [origin, setOrigin] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!apps.length || !spaceId) return null;

  const show = (e) => {
    // The button often sits inside a link to the product page.
    e.preventDefault();
    e.stopPropagation();
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      setOrigin({
        x: rect.left + rect.width / 2 - window.innerWidth / 2,
        y: rect.top + rect.height / 2 - window.innerHeight / 2,
      });
    }
    setOpen(true);
  };

  const openApp = (app) => {
    setOpen(false);
    navigate(`/mint-space/${spaceId}?nav=${app.navId}`);
  };

  const folded = { opacity: 0, scale: 0.15, x: origin.x, y: origin.y };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={show}
        className={
          buttonClassName ||
          `inline-flex items-center gap-1 rounded-md bg-black/55 px-1.5 py-0.5 text-caption-1-semibold text-white backdrop-blur-sm transition hover:bg-black/70 ${className}`
        }
      >
        <FiGrid className="h-3 w-3 shrink-0" />
        {t("mintSpace.viewAppsInside") || "View apps inside"}
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            // Clicks here must not reach a product link this sits in: React passes
            // events up through portals.
            <div
              className="fixed inset-0 z-[200] flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={title || t("mintSpace.viewAppsInside")}
            >
              <motion.div
                className="absolute inset-0 bg-black/45 backdrop-blur-xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22 }}
                onClick={() => setOpen(false)}
              />
              <motion.div
                className="relative w-full max-w-[19rem]"
                initial={folded}
                animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
                exit={folded}
                transition={{ type: "spring", stiffness: 380, damping: 32, mass: 0.8 }}
              >
                {title && (
                  <h2 className="mb-4 truncate px-2 text-center text-title-1 text-white drop-shadow">
                    {title}
                  </h2>
                )}
                <div className="rounded-[2rem] bg-white/15 p-5 shadow-2xl ring-1 ring-inset ring-white/20 backdrop-blur-2xl">
                  <div className="grid grid-cols-3 gap-x-3 gap-y-5">
                    {apps.map((app, i) => (
                      <motion.button
                        key={app.key}
                        type="button"
                        onClick={() => openApp(app)}
                        className="group flex min-w-0 flex-col items-center gap-1.5"
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.06 + i * 0.03, type: "spring", stiffness: 420, damping: 28 }}
                      >
                        <AppIcon app={app.appId} size={56} className="transition group-hover:scale-105 group-active:scale-95" />
                        <span className="w-full truncate text-center text-caption-1 font-medium text-white">
                          {app.name}
                        </span>
                      </motion.button>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default ProductAppsFolder;
